import analyticstracker from "@jmc/analyticstracker";
import { useLocale } from "@jmc/core/src/hooks/useLocale/index";
import { EventTypes } from "@jmc/core/src/types/EventTypes";
import {
    Icon,
    jnjArrowLeft,
    jnjArrowRight,
    jnjExternalLink,
    jnjLocation,
    jnjMail,
    jnjPhone,
} from "@jmc/solid-design-system/src/components/atoms/Icon/Icon";
import { LinkButton } from "@jmc/solid-design-system/src/components/atoms/LinkButton/LinkButton";
import { MediaQuery } from "@jmc/solid-design-system/src/components/atoms/MediaQuery/MediaQuery";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { Dialog } from "@jmc/solid-design-system/src/components/molecules/Dialog/Dialog";
import { BreakPoint, useMediaQuery } from "@jmc/solid-design-system/src/hooks/useMediaQuery/useMediaQuery";
import { getLocaleRtl } from "@jmc/solid-design-system/src/utils/languages";
import { useJnjBranding } from "@jmc/utils/hooks/useJnjBranding";
import { mdiCellphoneLink, mdiCloseCircleOutline, mdiEmailOutline, mdiMapMarker, mdiPhone } from "@mdi/js";
import classnames from "classnames";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { getMarkerId } from "../Geo";
import { useLocatorContext } from "../LocatorContext/LocatorContext";
import { showIcon } from "../utils/showIcon";
import style from "./style.module.scss";

interface TextBlockProps {
    title: string;
    value?: string;
    children?: JSX.Element | JSX.Element[];
    smallSpacing?: boolean;
}

const TextBlock = ({ title, value = null, children = null, smallSpacing = false }: TextBlockProps): JSX.Element => {
    const { jnjFullBranded } = useJnjBranding();
    return children || value ? (
        <div className={classnames(style.block, smallSpacing ? style.smallSpacing : null)}>
            <Typography
                color="light"
                className={style.title}
                size="s"
                weight="600"
                variant={jnjFullBranded ? "h6" : "body"}
                component="div"
            >
                {title}
            </Typography>
            {value && <Typography color="light">{value}</Typography>}
            {children}
        </div>
    ) : null;
};

const LinkField = ({
    icon,
    url,
    value,
    placement,
    name,
    external = true,
    component = null,
}: {
    icon: string;
    url: string;
    value: string;
    placement: string;
    targetUrl?: string;
    name: string;
    external?: boolean;
    component: JSX.Element;
}): JSX.Element => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const Component: any = component || "a";
    const { jnjFullBranded } = useJnjBranding();

    return value ? (
        <div className={style.fieldOneLine}>
            <Icon
                color="text-light"
                size={jnjFullBranded ? "medium" : "small"}
                icon={icon}
                type={jnjFullBranded ? "jnj" : "mdi"}
            />
            <Component external={external.toString()} url={url} placement={placement} name={name}>
                {value}
            </Component>
        </div>
    ) : null;
};

const TextField = ({ icon, value }: { icon: string; value: string | JSX.Element }): JSX.Element => {
    const { jnjFullBranded } = useJnjBranding();
    return value ? (
        <div className={style.fieldOneLine}>
            <Icon
                color="text-light"
                size={jnjFullBranded ? "medium" : "small"}
                icon={icon}
                type={jnjFullBranded ? "jnj" : "mdi"}
            />
            <Typography color="light">{value}</Typography>
        </div>
    ) : null;
};

interface props {
    name: string;
    placement: string;
    component: JSX.Element;
    hideScroll?: (val: boolean) => void;
}

export const InfoPanel = ({ name, placement, component = null, hideScroll }: props): JSX.Element => {
    const [SCROLL_THRESHOLD, setScrollThreshold] = useState(0);
    const { activeHit, setMapFullScreen, infoPanelVisible, setInfoPanelVisible } = useLocatorContext();
    const { t } = useTranslation();
    const { jnjFullBranded } = useJnjBranding();
    const locale = useLocale();
    const rtl = getLocaleRtl(locale);

    const isTablet = useMediaQuery(BreakPoint.lg);

    useEffect(() => {
        if (infoPanelVisible) {
            analyticstracker().trackEvent({
                event: EventTypes.MAP_IMPRESSION,
                info: {
                    component: "detail",
                    name,
                    placement,
                },
            });
            hideScroll && hideScroll(true);
        } else {
            hideScroll && hideScroll(false);
        }
    }, [infoPanelVisible]);

    const icon = showIcon(activeHit?.type, jnjFullBranded);

    useEffect(() => {
        if (activeHit !== null) {
            const title = document.getElementById(style["center-name"]);
            const control = document.getElementById(style["control-bar"]);
            const icon = document.getElementById(style["icon-section"]);
            setScrollThreshold(title?.offsetHeight + control?.offsetHeight + icon?.offsetHeight);
        }
    }, [activeHit]);

    const closePanel = (): void => {
        setInfoPanelVisible(false);
    };

    const showMap = (): void => {
        setTimeout(() => {
            // The map needs a little time to load, then we can center it
            const elem = document?.getElementById(getMarkerId(activeHit.objectID));
            elem && elem.click();
            setInfoPanelVisible(false);
        }, 200);
    };

    const showMapOnMobile = (): void => {
        setMapFullScreen(true);
        setTimeout(() => {
            // The map needs a little time to load, then we can center it
            const elem = document?.getElementById(getMarkerId(activeHit.objectID));
            elem && elem.click();
            setInfoPanelVisible(false);
        }, 200);
    };

    const handleScroll = (e: React.UIEvent<HTMLElement>): void => {
        const stickyTitle = document.getElementById(style["sticky-title"]);
        const classNames = stickyTitle.className.split(" ");

        if (e.currentTarget.scrollTop >= SCROLL_THRESHOLD && !classNames.includes(style.stickyVisible)) {
            stickyTitle.className = style.stickyVisible;
        }

        if (e.currentTarget.scrollTop < SCROLL_THRESHOLD && !classNames.includes(style.stickyHidden)) {
            stickyTitle.className = style.stickyHidden;
        }
    };

    const address =
        activeHit?.address1 || activeHit?.address2
            ? `${activeHit?.address1?.trim() || ""} ${activeHit?.address2?.trim() || ""}`?.trim()
            : t("Address not known", { ns: "common" });

    const fullAddress = address
        .concat(activeHit?.postal_code ? `, ${activeHit?.postal_code}` : "")
        .concat(activeHit?.city ? `${activeHit?.postal_code ? "" : ","} ${activeHit.city?.trim()}` : "")
        .concat(activeHit?.province ? `, ${activeHit.province?.trim()}` : "")
        .concat(activeHit?.country ? `, ${activeHit.country?.trim()}` : "");

    const PanelWrapper = ({ children }: { children: JSX.Element }) => {
        if (jnjFullBranded && !isTablet) {
            return (
                <Dialog onClose={closePanel} testId="Event.Question.Form.Dialog">
                    {children}
                </Dialog>
            );
        } else return <>{children}</>;
    };

    return infoPanelVisible ? (
        <PanelWrapper>
            <div className={style.infoPanel}>
                {(!jnjFullBranded || isTablet) && (
                    <div id={style["control-bar"]} className={style.controls}>
                        <div id={style["sticky-title"]} className={classnames(style.stickyHidden)}>
                            {activeHit?.name}
                        </div>
                        <button
                            className={style.infoPanel__closeButton}
                            onClick={closePanel}
                            title={t("Close", { ns: "common" })}
                            data-test-id="InfoPanel.Close"
                        >
                            {jnjFullBranded && isTablet ? (
                                <Icon size={"medium"} icon={rtl ? jnjArrowRight : jnjArrowLeft} type={"jnj"} />
                            ) : (
                                <Icon size={"large"} icon={mdiCloseCircleOutline} type={"mdi"} />
                            )}
                        </button>
                    </div>
                )}
                <div className={style.content} onScroll={handleScroll}>
                    <div className={style.header}>
                        <div id={style["icon-section"]} className={style.icon}>
                            <Icon
                                size={jnjFullBranded ? "large" : "xl"}
                                color="white"
                                icon={icon}
                                type={jnjFullBranded ? "jnj" : "mdi"}
                            />
                        </div>
                        <div id={style["center-name"]} className={style.name}>
                            <Typography font="title" size="xl" color="dark" weight="500">
                                {activeHit?.name}
                            </Typography>
                        </div>

                        <div className={style.centerAndSpecialty}>
                            {activeHit?.center_unit && (
                                <Typography size="m" color="light">
                                    {activeHit.center_unit}
                                </Typography>
                            )}
                            <Typography size="m" color="light">
                                {activeHit?.specialty}
                            </Typography>
                        </div>
                    </div>

                    <TextBlock title={t("Details", { ns: "common" })} smallSpacing={jnjFullBranded}>
                        <LinkField
                            icon={jnjFullBranded ? jnjMail : mdiEmailOutline}
                            url={`mailto:${activeHit?.email}`}
                            value={activeHit?.email}
                            name={name}
                            placement={placement}
                            targetUrl={`mailto:${activeHit?.email}`}
                            component={component}
                        />
                        <LinkField
                            icon={jnjFullBranded ? jnjExternalLink : mdiCellphoneLink}
                            url={activeHit?.website}
                            value={activeHit?.website}
                            name={name}
                            placement={placement}
                            targetUrl={`mailto:${activeHit?.website}`}
                            component={component}
                        />
                        <LinkField
                            icon={jnjFullBranded ? jnjPhone : mdiPhone}
                            url={`tel:${activeHit?.phone}`}
                            value={activeHit?.phone}
                            name={name}
                            placement={placement}
                            targetUrl={`tel:${activeHit?.phone}`}
                            component={component}
                        />

                        {jnjFullBranded ? (
                            <LinkButton onClick={showMap} data-test-id="InfoPanel.ShowMap" size="m">
                                <div className={style.fieldOneLine}>
                                    <Icon color="text-light" size={"medium"} icon={jnjLocation} type={"jnj"} />
                                    {fullAddress}
                                </div>
                            </LinkButton>
                        ) : (
                            <>
                                <MediaQuery min="md">
                                    <TextField icon={jnjFullBranded ? jnjLocation : mdiMapMarker} value={fullAddress} />
                                </MediaQuery>
                                <MediaQuery max="sm">
                                    <TextField
                                        icon={jnjFullBranded ? jnjLocation : mdiMapMarker}
                                        value={
                                            <LinkButton
                                                onClick={showMapOnMobile}
                                                data-test-id="InfoPanel.ShowMap"
                                                size="l"
                                            >
                                                {fullAddress}
                                            </LinkButton>
                                        }
                                    />
                                </MediaQuery>
                            </>
                        )}
                    </TextBlock>

                    <TextBlock title={t("Description", { ns: "common" })} value={activeHit?.description} />
                    <TextBlock title={t("Schedule time", { ns: "common" })} value={activeHit?.schedule_time} />

                    <TextBlock title={t("Center referent", { ns: "common" })}>
                        {activeHit?.center_referent && (
                            <>
                                <Typography color="light">{activeHit?.center_referent}</Typography>
                                <Typography color="light">{activeHit?.center_referent_role}</Typography>
                            </>
                        )}
                    </TextBlock>

                    <TextBlock title={t("Services", { ns: "common" })} value={activeHit?.services} />
                </div>
            </div>
        </PanelWrapper>
    ) : null;
};
